/**
 * @file use-overview-source.ts
 * @description Загрузка одного источника данных «Обзора» платформы
 * (phase19.29): состояние «нет права / загрузка / ошибка / готово» и
 * «Повторить».
 *
 * Тот же способ, что на страницах разделов (useEffect + try/catch), только
 * завёрнут в хук: на «Обзоре» три независимых источника, и сбой одного не
 * должен гасить остальные. Без права (`enabled = false`) запрос не
 * отправляется вовсе — сервер всё равно ответил бы 403.
 *
 * Текст ошибки в интерфейсе — всегда свой, понятный (`errorText`): сырые
 * сообщения сервера и сети («Failed to fetch», «HTTP 502», «Перейдите на
 * /select-context») владельцу ничего не говорят; техническая причина
 *  уходит в консоль (console.info).
 *
 * 403 `rbac_no_active_context` сразу после входа (серверный недочёт TD-110:
 * выбранный кабинет попадает в сессию только после перечитывания
 * /auth/me) — не ошибка: AuthProvider на этот ответ сам перечитывает
 * /auth/me, активный контекст в кабинете обновляется (`contextKey` — новый
 * объект), и источник, который ЖДЁТ контекст, перезапрашивается. Источники
 * с готовыми данными на смену контекста не реагируют — иначе каждый 403
 * соседей (каждый вызывает перечитывание /auth/me) перезапускал бы все
 * запросы по кругу. Пока идёт восстановление — скелет. Страховка — повтор
 * по таймеру, если контекст обновился раньше, чем пришёл ответ.
 * Автоповторов не больше `MAX_CONTEXT_RETRIES`, без бесконечного цикла.
 *
 * `load` должен быть стабильной ссылкой (функция модуля или useCallback).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../../../lib/api-client";

export type OverviewSource<T> =
  | { status: "disabled" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: T };

/** Сколько раз подряд автоматически повторять запрос при «нет активного кабинета». */
export const MAX_CONTEXT_RETRIES = 2;
/** Страховочный повтор, если обновлённый контекст не пришёл. */
export const CONTEXT_RETRY_MS = 1500;

/** Ответ «активный кабинет не выбран в сессии» — восстанавливается сам. */
function isNoActiveContext(err: unknown): boolean {
  return err instanceof ApiError && err.code === "rbac_no_active_context";
}

export function useOverviewSource<T>(
  enabled: boolean,
  load: () => Promise<T>,
  errorText: string,
  /** Меняется, когда AuthProvider перечитал /auth/me (активный контекст). */
  contextKey?: unknown,
): { source: OverviewSource<T>; reload: () => void } {
  const [source, setSource] = useState<OverviewSource<T>>(
    enabled ? { status: "loading" } : { status: "disabled" },
  );
  // Счётчик попыток: «Повторить» и страховочный таймер увеличивают его.
  const [attempt, setAttempt] = useState(0);
  // Сколько автоповторов уже было из-за «нет активного кабинета».
  const contextRetries = useRef(0);
  // Источник ждёт обновлённый контекст (получил 403 «нет активного кабинета»).
  const waitingForContext = useRef(false);

  // Контекст обновился — повторить только тот источник, который его ждёт.
  useEffect(() => {
    if (waitingForContext.current) setAttempt((n) => n + 1);
  }, [contextKey]);

  useEffect(() => {
    if (!enabled) {
      setSource({ status: "disabled" });
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Запрос ушёл — до ответа новые смены контекста его не перезапускают.
    waitingForContext.current = false;
    // Готовые данные при перезапросе не прячем за скелетом.
    setSource((prev) => (prev.status === "ready" ? prev : { status: "loading" }));
    load()
      .then((data) => {
        if (cancelled) return;
        contextRetries.current = 0;
        setSource({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isNoActiveContext(err) && contextRetries.current < MAX_CONTEXT_RETRIES) {
          // Не ошибка: ждём обновлённый контекст (эффект перезапустится по
          // contextKey), а если он уже пришёл раньше ответа — повтор по таймеру.
          contextRetries.current += 1;
          waitingForContext.current = true;
          setSource({ status: "loading" });
          timer = setTimeout(() => setAttempt((n) => n + 1), CONTEXT_RETRY_MS);
          return;
        }
        // Техническая причина — только в диагностику, в интерфейс — свой текст.
        // (console.info — разрешённый линтером уровень диагностики.)
        console.info("[overview] источник не загрузился:", err);
        setSource({ status: "error", message: errorText });
      });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, load, errorText, attempt]);

  const reload = useCallback(() => {
    // Ручной повтор — снова разрешаем автоповторы.
    contextRetries.current = 0;
    setAttempt((n) => n + 1);
  }, []);
  return { source, reload };
}
